import { useEffect, useRef, useState } from 'react'
import { ThemeProvider } from '@opallabs/hooks'
import { kuiperClassNames } from '@opallabs/kuiper'
import { formatFilesize } from '@opallabs/lambda'
import { Button, ButtonSize, Icon } from '@opallabs/lapidary'
import { matchContentPath } from '@opallabs/routing'
import {
  connectPath,
  fetchConnectionStatus,
  fetchPreviewUrl,
  importErrorMessage,
  importSharepointDocument,
  isForbiddenError,
  isNotConnectedError,
  refreshSharepointDocument,
  SharepointDocument as SharepointDocumentData,
} from './api'
import * as styles from './styles'
import { SharepointDocumentProps } from './types'

type ImportState = 'idle' | 'importing' | 'error'

const CONNECT_POPUP = 'sharepoint_oauth'

// The card stores its data in these post options; clearing them removes the
// document. Names must match the option system_names in services/sharepoint.json.
const OPTION_NAMES = [
  'sharepoint_web_url',
  'sharepoint_file_name',
  'sharepoint_mime_type',
  'sharepoint_file_size',
  'sharepoint_drive_id',
  'sharepoint_item_id',
  'sharepoint_etag',
] as const

// Unset post options arrive as null (not undefined), so default parameter
// values don't apply — every field must tolerate null.
const fileExtensionLabel = (
  fileName?: string | null,
  mimeType?: string | null
): string => {
  const extension = fileName?.includes('.') ? fileName.split('.').pop() : null
  return extension ? extension.toUpperCase() : (mimeType ?? '')
}

// Office documents open straight into their web editor from webUrl, so the
// link is an edit affordance for them; everything else just opens SharePoint.
const OFFICE_APPS: Record<string, string> = {
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'PowerPoint',
}

const openLinkLabel = (mimeType?: string | null): string => {
  const app = mimeType ? OFFICE_APPS[mimeType] : undefined
  return app ? `Edit in ${app}` : 'Open in SharePoint'
}

// Each updateText re-renders synchronously, and the card flips to its filled
// state as soon as the web URL is present — write it last so the filled card
// never renders with the other fields missing.
const writeDocumentOptions = (
  document: SharepointDocumentData,
  updateText?: (value: string, destination: string) => void
): void => {
  updateText?.(document.name, 'sharepoint_file_name')
  updateText?.(document.mimeType, 'sharepoint_mime_type')
  updateText?.(document.size, 'sharepoint_file_size')
  updateText?.(document.driveId, 'sharepoint_drive_id')
  updateText?.(document.itemId, 'sharepoint_item_id')
  updateText?.(document.etag, 'sharepoint_etag')
  updateText?.(document.webUrl, 'sharepoint_web_url')
}

export const SharepointDocument: React.FC<SharepointDocumentProps> = ({
  isFullscreen = false,
  isPresenting = false,
  isSnapshot = false,
  readOnly = false,
  sharepointDriveId = '',
  sharepointEtag = '',
  sharepointFileName = '',
  sharepointFileSize = '',
  sharepointItemId = '',
  sharepointMimeType = '',
  sharepointWebUrl = '',
  onToggleFullscreen = (): void => undefined,
  updateText,
}) => {
  const [url, setUrl] = useState('')
  const [importState, setImportState] = useState<ImportState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewNotConnected, setPreviewNotConnected] = useState(false)
  const [previewForbidden, setPreviewForbidden] = useState(false)
  // null until the status check settles; the empty composer card shows
  // nothing in the meantime rather than flashing the paste form.
  const [isConnected, setIsConnected] = useState<boolean | null>(null)
  const [connectRetry, setConnectRetry] = useState(0)
  const hasRefreshed = useRef(false)
  const connectPoll = useRef<number | undefined>(undefined)

  const isComposer = !isPresenting && !isSnapshot && !readOnly

  // The preview URL is per-viewer and short-lived, so it's fetched on mount
  // and never stored. Opal keeps no rendering of the file, so a viewer whose
  // fetch fails sees the file icon; one who isn't connected or lacks
  // SharePoint access is told why, everything else stays silent.
  useEffect(() => {
    setPreviewUrl('')
    setPreviewNotConnected(false)
    setPreviewForbidden(false)
    if (!sharepointWebUrl || isSnapshot) {
      return
    }
    let cancelled = false
    fetchPreviewUrl({
      driveId: sharepointDriveId,
      itemId: sharepointItemId,
      url: sharepointWebUrl,
    })
      .then((getUrl) => {
        if (!cancelled) {
          setPreviewUrl(getUrl)
        }
      })
      .catch((caught) => {
        if (cancelled) {
          return
        }
        if (isNotConnectedError(caught)) {
          setPreviewNotConnected(true)
        } else if (isForbiddenError(caught)) {
          setPreviewForbidden(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [
    sharepointWebUrl,
    sharepointDriveId,
    sharepointItemId,
    isSnapshot,
    connectRetry,
  ])

  // The empty composer card checks the connection before offering the paste
  // form, so nobody pastes a link only to learn they can't import it.
  useEffect(() => {
    if (!isComposer || sharepointWebUrl) {
      return
    }
    let cancelled = false
    setIsConnected(null)
    fetchConnectionStatus().then((connected) => {
      if (!cancelled) {
        setIsConnected(connected)
      }
    })
    return () => {
      cancelled = true
    }
  }, [isComposer, sharepointWebUrl, connectRetry])

  useEffect(() => (): void => window.clearInterval(connectPoll.current), [])

  // Refresh once per composer view: re-resolve the document and rewrite the
  // stored fields only when the file's eTag changed in SharePoint, so a
  // renamed or replaced file shows its current name and size.
  useEffect(() => {
    if (hasRefreshed.current || !isComposer || !sharepointWebUrl) {
      return
    }
    const postId = matchContentPath(
      location.pathname.replace('/legacy', '')
    )?.id
    if (!postId) {
      return
    }
    hasRefreshed.current = true
    refreshSharepointDocument(
      postId,
      {
        driveId: sharepointDriveId,
        itemId: sharepointItemId,
        url: sharepointWebUrl,
      },
      sharepointEtag
    )
      .then(({ changed, document }) => {
        if (changed) {
          writeDocumentOptions(document, updateText)
        }
      })
      .catch(() => undefined)
  }, [
    isComposer,
    sharepointWebUrl,
    sharepointDriveId,
    sharepointItemId,
    sharepointEtag,
    updateText,
  ])

  const handleRemove = (): void => {
    OPTION_NAMES.forEach((name) => updateText?.('', name))
  }

  // The OAuth callback page closes itself once the account is connected, so
  // the popup closing (after the viewer finishes — or abandons — the flow) is
  // the signal to re-check the connection and retry the preview. An abandoned
  // flow just 412s again and the connect affordance stays.
  const handleConnect = (): void => {
    const popup = window.open(
      connectPath,
      CONNECT_POPUP,
      'popup,width=600,height=760'
    )
    if (!popup) {
      return
    }
    window.clearInterval(connectPoll.current)
    connectPoll.current = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(connectPoll.current)
        setConnectRetry((count) => count + 1)
      }
    }, 500)
  }

  const handleImport = async (): Promise<void> => {
    const postId = matchContentPath(
      location.pathname.replace('/legacy', '')
    )?.id
    if (!postId || !url.trim()) {
      return
    }

    setImportState('importing')
    setError(null)

    try {
      const { document } = await importSharepointDocument(url.trim(), postId)
      writeDocumentOptions(document, updateText)
      setUrl('')
      setImportState('idle')
    } catch (caught) {
      if (isNotConnectedError(caught)) {
        // The pasted URL stays in state, so the form comes back filled in
        // once the viewer connects.
        setIsConnected(false)
        setImportState('idle')
      } else {
        setError(importErrorMessage(caught))
        setImportState('error')
      }
    }
  }

  // Live preview for viewers SharePoint lets in; the file icon for everyone
  // else, including snapshots and exports, which never fetch. Deliberately no
  // stored image: a viewer Opal can't vouch for sees nothing of the file.
  const renderPreview = (variant: 'card' | 'pane') => {
    if (previewUrl) {
      return (
        <iframe
          allowFullScreen={true}
          css={variant === 'pane' ? styles.panePreview : styles.preview}
          sandbox="allow-scripts allow-forms allow-presentation allow-same-origin allow-popups"
          src={previewUrl}
          title={sharepointFileName || 'SharePoint document'}
        />
      )
    }
    return (
      <div css={styles.iconContainer}>
        <Icon glyph="gem-file" ariaHidden />
      </div>
    )
  }

  const renderDetails = () => {
    const sizeLabel = sharepointFileSize
      ? formatFilesize(Number(sharepointFileSize))
      : ''
    const typeLabel = fileExtensionLabel(sharepointFileName, sharepointMimeType)

    return (
      <>
        <p css={styles.fileName}>
          {sharepointFileName || 'SharePoint document'}
        </p>
        <p css={styles.fileMeta}>
          {[typeLabel, sizeLabel].filter(Boolean).join(' · ')}
        </p>
        {previewForbidden && !isPresenting ? (
          <p css={styles.fileMeta}>
            You don&apos;t have access to this file in SharePoint.
          </p>
        ) : null}
      </>
    )
  }

  const renderActions = () => (
    <div css={styles.actions}>
      <a
        css={styles.openLink}
        href={sharepointWebUrl ?? undefined}
        rel="noopener noreferrer"
        target="_blank"
      >
        {openLinkLabel(sharepointMimeType)}
      </a>
      {/* Not shown while presenting: a personal OAuth prompt is noise on a
          projected screen. */}
      {previewNotConnected && !isPresenting ? (
        <Button
          name="sharepoint-connect-preview"
          onClick={handleConnect}
          size={ButtonSize.Small}
          variant="ghost"
        >
          Connect Microsoft to preview
        </Button>
      ) : null}
      {isComposer ? (
        <Button
          name="sharepoint-remove"
          onClick={handleRemove}
          size={ButtonSize.Small}
          variant="ghost"
        >
          Remove
        </Button>
      ) : null}
    </div>
  )

  const renderConnectPrompt = () => (
    <div css={styles.paste}>
      <p css={styles.pasteLabel}>
        Connect your Microsoft account to add a SharePoint file.
      </p>
      <Button
        name="sharepoint-connect"
        onClick={handleConnect}
        size={ButtonSize.Small}
      >
        Connect Microsoft
      </Button>
    </div>
  )

  const renderPasteForm = () => (
    <div css={styles.paste}>
      <label css={styles.pasteLabel} htmlFor="sharepoint-url">
        Paste a SharePoint file link
      </label>
      <input
        css={styles.pasteInput}
        id="sharepoint-url"
        onChange={(event): void => setUrl(event.target.value)}
        placeholder="https://yourcompany.sharepoint.com/…"
        type="url"
        value={url}
      />
      <Button
        disabled={importState === 'importing' || !url.trim()}
        name="sharepoint-import"
        onClick={handleImport}
        size={ButtonSize.Small}
      >
        {importState === 'importing' ? 'Importing…' : 'Import'}
      </Button>
      {error ? <p css={styles.error}>{error}</p> : null}
    </div>
  )

  // In the composer the document takes over the post shroud's content area
  // the way the Opal Document editor does (see Content/All/Document), with
  // the same toggle to expand over the sidebar. Everywhere else — content
  // cards, presentations, snapshots — the compact card below renders, since
  // a fixed pane has no meaning inside a grid or an export.
  if (isComposer) {
    return (
      <>
        <div css={styles.expandToggle(isFullscreen)}>
          <ThemeProvider theme="dark">
            <Button
              css={styles.expandButton}
              hideButtonText
              icon={
                <Icon
                  ariaHidden
                  css={styles.expandIcon(isFullscreen)}
                  glyph="gem-indent"
                />
              }
              name="sharepoint-expand-toggle"
              onClick={(): void => onToggleFullscreen(!isFullscreen)}
              size={ButtonSize.Small}
              variant="tertiary"
            />
          </ThemeProvider>
        </div>
        <div
          className={kuiperClassNames('sharepointDocument')}
          css={styles.pane(isFullscreen)}
        >
          {sharepointWebUrl ? (
            <>
              <header css={styles.paneHeader}>
                <div css={styles.paneTitle}>{renderDetails()}</div>
                {renderActions()}
              </header>
              <div css={styles.paneBody}>{renderPreview('pane')}</div>
            </>
          ) : (
            <div css={styles.paneEmpty}>
              {isConnected === false
                ? renderConnectPrompt()
                : isConnected
                  ? renderPasteForm()
                  : null}
            </div>
          )}
        </div>
      </>
    )
  }

  if (sharepointWebUrl) {
    return (
      <div className={kuiperClassNames('sharepointDocument')} css={styles.card}>
        {renderPreview('card')}
        <div css={styles.details}>
          {renderDetails()}
          {renderActions()}
        </div>
      </div>
    )
  }

  return (
    <div
      className={kuiperClassNames('sharepointDocument')}
      css={styles.placeholder}
    >
      No SharePoint document yet.
    </div>
  )
}
